import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import '@wtfalch/design/tokens.css';
import '@wtfalch/design/styles.css';
import './tailwind.css';
import './app.css';

import { productCss } from '@wtfalch/design';
import { GOOD_COMPANY_FONTS } from '@wtfalch/house';
import { App } from './App.tsx';
import { CallSessionProvider } from './calls/CallSession.tsx';
import { GuestCall } from './calls/GuestCall.tsx';
import { applyAppIcon, savedAppIcon } from './design/appIcon.ts';
import { bootTheme } from './design/bootTheme.ts';
import { product } from './design/product.ts';
import { BUILD_RELEASE, NewVersionBanner } from './release/NewVersionBanner.tsx';
import { registerServiceWorker } from './release/registerServiceWorker.ts';
import { installReleaseGate } from './release/releaseGate.ts';
import { parse } from './routes.ts';

/* Good Company, from `@wtfalch/house`. Each company's colour is not a theme:
   the shell draws it as a swatch beside the company's name (`shell/AgoraShell.tsx`).

   `productCss` carries the identity and every palette; a Vite app has no
   server render to put it in, so it goes in the head here, before the first
   paint. The faces come from the same package, so the URL is written once in
   the estate. */
const style = document.createElement('style');
style.textContent = productCss(product);
document.head.append(style);

const fonts = document.createElement('link');
fonts.rel = 'stylesheet';
fonts.href = GOOD_COMPANY_FONTS;
document.head.append(fonts);

// `productCss` above already puts every theme's palette on the page, keyed
// by `data-theme` -- the attribute is the whole mechanism (`ThemeSwitch`'s
// own docblock in `@wtfalch/design`). `applyTheme` writes the same palette a
// second way, as inline custom properties on `<html>`, and inline beats an
// attribute selector: a switch to another theme would set `data-theme` and
// nothing would move, because the first theme's values were still pinned
// inline. Only the attribute belongs at boot.
bootTheme();
applyAppIcon(savedAppIcon(), false);

const root = document.getElementById('root');
if (!root) throw new Error('index.html has no #root');

registerServiceWorker();
// A forced update's check needs a live Caddy in front -- dev talks to local
// services directly, so there is nothing for the header to reach.
if (!import.meta.env.DEV) installReleaseGate(BUILD_RELEASE);
const reactRoot = createRoot(root);

if (import.meta.env.DEV && window.location.pathname === '/__preview') {
  void import('./preview.tsx').then(({ Preview }) =>
    reactRoot.render(
      <StrictMode>
        <Preview />
      </StrictMode>,
    ),
  );
} else if (parse(window.location.pathname).kind === 'guest-call') {
  // A guest has no company sign-in: this renders before `App`, which reads the
  // member's saved sessions, and the call session around it is never made.
  reactRoot.render(
    <StrictMode>
      <GuestCall />
    </StrictMode>,
  );
} else {
  reactRoot.render(
    <StrictMode>
      <CallSessionProvider>
        <App />
        <NewVersionBanner />
      </CallSessionProvider>
    </StrictMode>,
  );
}
